import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import braUfCities from '../../../assets/json/braUfCities.json';
import { Container, Row, Col, Card, Button, Form, Spinner, Modal } from 'react-bootstrap';
import PageTitle from '../../common/PageTitle';
import { apiListBases, apiExportBases } from '../../../api/api';
import { useUser } from '../../../context/UserContext';
import { useToast } from '../../../context/ToastContext';

const BuyPopup = ({ show, handleBuy }) => (
  <Modal
    show={show}
    backdrop="static"
    keyboard={false}
    fullscreen={false}
    aria-labelledby="contained-modal-title-vcenter"
    centered>
    <Modal.Body >
      <div>
        <h1>Olá!</h1>
        <p>O download das bases de dados está disponível somente em algum dos planos contratados.</p>
      </div>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="primary" onClick={handleBuy}>
        CONTRATAR
      </Button>
    </Modal.Footer>
  </Modal>
);

const ExportProgressModal = ({ show, currentStep, downloadStarted, onCancel, onClose }) => {
  const steps = [
    'Validando seleção',
    'Preparando dados',
    'Gerando planilha',
    'Iniciando download',
  ];

  return (
    <Modal show={show} backdrop="static" keyboard={false} centered>
      <Modal.Header style={{ backgroundColor: '#FF6633' }}>
        <Modal.Title className='text-white'>Exportando base</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='mb-3'>
          <div className='progress' style={{ height: '10px' }}>
            <div
              className='progress-bar progress-bar-striped progress-bar-animated bg-warning'
              role='progressbar'
              style={{ width: `${Math.min(((currentStep + 1) / steps.length) * 100, 100)}%` }}
              aria-valuenow={Math.min(currentStep + 1, steps.length)}
              aria-valuemin='0'
              aria-valuemax={steps.length}
            />
          </div>
        </div>
        {steps.map((step, index) => (
          <div key={step} className='d-flex align-items-center justify-content-between mb-2'>
            <div className={index === currentStep ? 'fw-bold' : 'text-muted'}>
              {index + 1}. {step}
            </div>

            {index < currentStep && (
              <span className='badge bg-success'>Concluido</span>
            )}

            {index === currentStep && !downloadStarted && (
              <span className='badge bg-warning text-dark d-inline-flex align-items-center'>
                <Spinner animation='border' size='sm' className='me-2' />
                Processando
              </span>
            )}

            {index > currentStep && (
              <span className='badge bg-secondary'>Aguardando</span>
            )}
          </div>
        ))}
        {downloadStarted && (
          <div className='fw-bold text-success mt-3'>Download iniciado. Você já pode acompanhar no navegador.</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!downloadStarted && (
          <Button variant='secondary' onClick={onCancel}>
            CANCELAR
          </Button>
        )}
        {downloadStarted && (
          <Button variant='primary' onClick={onClose}>
            FECHAR
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const Bases = () => {
  const { user, updateUser } = useUser();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedBase, setSelectedBase] = useState('');
  const [selectedRelease, setSelectedRelease] = useState('');

  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [exportAbortController, setExportAbortController] = useState(null);

  const [bases, setBases] = useState([]);
  const [releases, setReleases] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setBases([]);
      setReleases([]);
      try {
        const response = await apiListBases(selectedRegion);
        if (response && Array.isArray(response)) {
          setBases(response);
        }
      } catch (error) {
        addToast('Erro ao buscar bases');
      }
    };
    if (selectedRegion) {
      fetchData();
    }
  }, [selectedRegion, addToast]);

  useEffect(() => {
    const fetchData = async () => {
      setReleases([]);
      const base = bases.find(base => base.name === selectedBase.toString());
      if (base) {
        const sortedReleases = base.Releases.sort((a, b) => {
          if (a.year === b.year) {
            return b.month - a.month;
          }
          return b.year - a.year;
        });
        setReleases(sortedReleases);
      }
    };
    fetchData();
  }, [selectedBase, bases]);

  useEffect(() => () => {
    if (exportAbortController) {
      exportAbortController.abort();
    }
  }, [exportAbortController]);

  const resetExportState = () => {
    setShowProgressModal(false);
    setDownloadStarted(false);
    setCurrentStep(0);
    setIsLoading(false);
    setExportAbortController(null);
  };

  const handleCancelExport = () => {
    if (exportAbortController) {
      exportAbortController.abort();
    }
    addToast('Exportacao cancelada');
    resetExportState();
  };

  const handleCloseExportModal = () => {
    resetExportState();
  };

  const handleDownloadClick = () => {
    const fetchData = async () => {
      const controller = new AbortController();
      setExportAbortController(controller);
      setIsLoading(true);
      setShowProgressModal(true);
      setDownloadStarted(false);
      setCurrentStep(0);
      const releaseId = Number(selectedRelease);
      let hasStartedDownload = false;
      const data = {
        userId: user.id,
        full: true,
        costGroups: false,
        fic: false,
        elements: false,
        equipments: false,
        services: false,
        resources: false,
        taxes: false,
      };
      try {
        setCurrentStep(1);
        const response = await apiExportBases(releaseId, selectedRegion, data, controller.signal);
        if (response.ok) {
          setCurrentStep(2);
          const disposition = response.headers.get('Content-Disposition') || '';
          const match = disposition.match(/filename="?([^";]+)"?/);
          const rel = releases.find((r) => r.id === releaseId);
          const fallback = rel
            ? `${selectedBase}-${selectedRegion}_${rel.year}_${String(rel.month).padStart(2, '0')}.xlsx`
            : `${selectedBase}-${selectedRegion}.xlsx`;
          const fileName = match ? match[1] : fallback;

          const blob = await response.blob();
          setCurrentStep(3);
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          hasStartedDownload = true;
          setCurrentStep(3);
          setDownloadStarted(true);
          link.remove();
          window.URL.revokeObjectURL(url);

          const updatedUser = { ...user, searches: (user.searches || 0) + 1 };
          updateUser(updatedUser);

        } else {
          const json = await response.json();
          addToast(json.message || json.error || 'Erro ao exportar bases');
        }
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }
        console.error('Erro ao exportar bases:', error);
        addToast('Erro ao exportar bases');
      } finally {
        setExportAbortController(null);
        if (!hasStartedDownload) {
          setShowProgressModal(false);
          setDownloadStarted(false);
          setCurrentStep(0);
        }
        setIsLoading(false);
      }
    };

    if (user.subscription === 0) {
      setShowPopup(true);
    } else {
      if (!isLoading) {
        fetchData();
      }
    }
  };

  return (
    <>
      <Container>
        <Row>
          <Col sm={6}>
            <PageTitle text='bases de dados' />
          </Col>
        </Row>
      </Container>

      <Container className=''>
        <Row className="justify-content-md-center">
          <Col className='mb-2' sm={6}>
            <Card>
              <Card.Header className='bg-secondary text-white'>#1 - Praça de Cotação</Card.Header>
              <Card.Body>
                <Card.Subtitle className="mb-2 text-muted">Selecione a praça de cotação.</Card.Subtitle>
                <Form.Select
                  size="sm"
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  disabled={isLoading}
                >
                  <option key='' value=''>Selecione uma praça</option>
                  {braUfCities.map((uf) => (
                    <option key={uf.sigla} value={uf.sigla}>
                      {uf.sigla} / {uf.nome}
                    </option>
                  ))}
                </Form.Select>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="justify-content-md-center">
          <Col className='mb-2' sm={6}>
            <Card>
              <Card.Header className='bg-secondary text-white'>#2 - Bases de Dados</Card.Header>
              <Card.Body>
                <Card.Subtitle className="mb-2 text-muted">Selecione a base de dados que deseja exportar.</Card.Subtitle>
                <Form.Select
                  size="sm"
                  onChange={(e) => setSelectedBase(e.target.value)}
                  disabled={isLoading}
                >
                  <option value=''>Selecione uma base</option>
                  {bases.map((base, index) => (
                    <option key={index} value={base.name}>{base.name}</option>
                  ))}
                </Form.Select>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="justify-content-md-center">
          <Col className='mb-2' sm={6}>
            <Card>
              <Card.Header className='bg-secondary text-white'>#3 - Data</Card.Header>
              <Card.Body>
                <Card.Subtitle className="mb-2 text-muted">Selecione a data de referência.</Card.Subtitle>
                <Form.Select
                  size="sm"
                  value={selectedRelease}
                  onChange={(e) => setSelectedRelease(e.target.value)}
                  disabled={isLoading}
                >
                  <option value=''>Selecione uma versão</option>
                  {releases.map((release) => (
                    <option key={release.id} value={release.id}>
                      {release.year}/{release.month.toString().padStart(2, '0')}
                    </option>
                  ))}
                </Form.Select>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="justify-content-md-center">
          <Col className='mb-2' sm={6}>
            <Card>
              <Card.Body>
                <div className='d-grid'>
                  <Button
                    variant='primary'
                    size='lg'
                    disabled={isLoading || !selectedRegion || !selectedBase || !selectedRelease}
                    onClick={handleDownloadClick}
                  >
                    {isLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'FAZER DOWNLOAD'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <BuyPopup show={showPopup} handleBuy={() => navigate('/main/plans')} />
        <ExportProgressModal
          show={showProgressModal}
          currentStep={currentStep}
          downloadStarted={downloadStarted}
          onCancel={handleCancelExport}
          onClose={handleCloseExportModal}
        />
      </Container>
    </>
  );
};

export default Bases;